import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Setup axios interceptor to add Bearer token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({});

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data.language) {
        setLanguage(response.data.language);
      }
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTranslations = useCallback(async (lang) => {
    try {
      const response = await axios.get(`${API}/translations/${lang}`);
      setTranslations(response.data);
    } catch {
      // Fallback to English
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    loadTranslations(language);
  }, [language, loadTranslations]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    const { access_token, refresh_token, ...userData } = response.data;
    setUser(userData);
    if (response.data.language) {
      setLanguage(response.data.language);
    }
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    const { access_token, refresh_token, ...userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // Ignore errors
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  const updateProfile = async (data) => {
    await axios.put(`${API}/auth/profile`, data);
    if (data.language) {
      setLanguage(data.language);
    }
    await checkAuth();
  };

  const uploadLogo = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/auth/upload-logo`, formData, { 
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    await checkAuth();
    return response.data;
  };

  const t = (key) => {
    // Support dot notation for nested keys (e.g., 'generate.title')
    const parts = key.split('.');
    let result = translations;
    for (const part of parts) {
      if (result && typeof result === 'object') {
        result = result[part];
      } else {
        return key;
      }
    }
    // If result is still an object, return the key string instead
    if (result && typeof result === 'object') return key;
    return result || key;
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, logout, updateProfile, uploadLogo,
      language, setLanguage, translations, t 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
